import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { authService } from '../services/auth';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const { 
    isAuthenticated: isAuth0Authenticated, 
    isLoading: isAuth0Loading,
    user: auth0User,
    getAccessTokenSilently,
    getIdTokenClaims
  } = useAuth0();
  
  const [user, setUser] = useState(null);
  const [isMfaVerified, setIsMfaVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize user from Auth0 authentication
  useEffect(() => {
    const initializeUser = async () => {
      if (isAuth0Loading) return;
      
      try {
        setLoading(true);
        
        // If user is authenticated with Auth0
        if (isAuth0Authenticated && auth0User) {
          console.log('Auth0 authenticated, exchanging tokens with backend');
          
          // Exchange Auth0 tokens with Xano
          const [accessToken, idTokenClaims] = await Promise.all([
            getAccessTokenSilently(),
            getIdTokenClaims()
          ]);
          
          if (accessToken && idTokenClaims?.__raw) {
            try {
              // Exchange tokens with backend
              await authService.exchangeAuth0Tokens(accessToken, idTokenClaims.__raw);
              
              // Get user profile from backend
              const userProfile = await authService.getUserProfile();
              setUser(userProfile);
              
              // Check if user has completed MFA
              // This will be stored in session until logout
              const mfaCompleted = sessionStorage.getItem('mfa_verified') === 'true';
              setIsMfaVerified(mfaCompleted);
            } catch (error) {
              console.error('Failed to exchange tokens:', error);
              setUser(null);
              setIsMfaVerified(false);
            }
          }
        } else if (!isAuth0Authenticated) {
          // Clear user if not authenticated with Auth0
          setUser(null);
          setIsMfaVerified(false);
          sessionStorage.removeItem('mfa_verified');
          authService.clearToken();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setLoading(false);
      }
    };
    
    initializeUser();
  }, [isAuth0Authenticated, isAuth0Loading, auth0User]);
  
  // Method to complete MFA verification
  const completeMfaVerification = () => {
    setIsMfaVerified(true);
    sessionStorage.setItem('mfa_verified', 'true');
  };
  
  // Check if user is fully authenticated (Auth0 + MFA)
  const isFullyAuthenticated = isAuth0Authenticated && isMfaVerified;
  
  const value = {
    user,
    isAuth0Authenticated,
    isMfaVerified,
    isFullyAuthenticated,
    loading: loading || isAuth0Loading,
    completeMfaVerification
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}